import { PaginationResponse, PagedStatsResponse } from "../../Shared/Pagination/PaginationResponse";

/** The waitlist track a registration lands in. Mirrors the backend AccessTrack enum. */
export type AccessTrack = 'NORMAL' | 'EARLY_ACCESS' | 'CANDIDATE_REFERRAL' | 'ADMIN_REFERRAL';

/** The single registration option an invite grants. */
export type TargetRole = 'CANDIDATE' | 'RECRUITER';

export interface AccessTrackOption {
  value: AccessTrack;
  label: string;
  description: string;
  waitlistStatus: string;
  bonusPoints: number;
}

export const ACCESS_TRACK_OPTIONS: AccessTrackOption[] = [
  {
    value: 'NORMAL',
    label: 'Normal Waitlist',
    description: 'Standard review queue. Approve to grant normal portal access.',
    waitlistStatus: 'UNDER_REVIEW',
    bonusPoints: 0,
  },
  {
    value: 'EARLY_ACCESS',
    label: 'Early Access',
    description: 'Founder member queue. Approve to send the founder member welcome email.',
    waitlistStatus: 'EARLY_ACCESS_PENDING',
    bonusPoints: 0,
  },
  {
    value: 'CANDIDATE_REFERRAL',
    label: 'Candidate Referral',
    description: 'Referred by a candidate. The referrer earns a profile visibility boost.',
    waitlistStatus: 'UNDER_REVIEW',
    bonusPoints: 10,
  },
  {
    value: 'ADMIN_REFERRAL',
    label: 'Admin Referral',
    description: 'Sourced directly by the admin team. No bonus is granted.',
    waitlistStatus: 'UNDER_REVIEW',
    bonusPoints: 0,
  },
];

export const ACCESS_TRACK_LABEL: Record<AccessTrack, string> = {
  NORMAL: 'Normal Waitlist',
  EARLY_ACCESS: 'Early Access',
  CANDIDATE_REFERRAL: 'Candidate Referral',
  ADMIN_REFERRAL: 'Admin Referral',
};

/** Tracks a given target role is allowed to use, matching the backend AccessTrack rules. */
export const accessTracksForRole = (role: TargetRole): AccessTrackOption[] => {
  if (role === 'RECRUITER') {
    return ACCESS_TRACK_OPTIONS.filter(
      (option) => option.value !== 'CANDIDATE_REFERRAL',
    );
  }
  return ACCESS_TRACK_OPTIONS;
};

export interface PortalConfig {
  defaultAccessTrack: string;
  earlyAccessEnabled: boolean;
  candidateReferralEnabled: boolean;
  adminReferralEnabled: boolean;
  candidateInviteEnabled: boolean;
  recruiterInviteEnabled: boolean;
  lockRoleToInvite: boolean;
  candidateReferralBonusPoints: number;
  adminReferralBonusPoints: number;
  earlyAccessFounderLabel: string;
}

export interface GrowthOverview {
  totalVisitors: number;
  uniqueVisitors: number;
  linkedInVisitors: number;
  organicSearchVisitors: number;
  communityVisitors: number;
  referralVisitors: number;
  registrationStarted: number;
  registrationCompleted: number;
  approvedCandidates: number;
  registrationConversionPct: number;
  approvalPct: number;
  referralConversionPct: number;
  totalCandidates: number;
  growthSeries: number[];
  totalProspects: number;
  invitationsSent: number;
}

export interface FunnelStage {
  name: string;
  count: number;
  conversionPct: number;
}

export interface GrowthFunnel {
  stages: FunnelStage[];
}

export interface GrowthSource {
  source: string;
  visitors: number;
  registrationStarted: number;
  registered: number;
  approved: number;
  conversionPct: number;
  /** The medium most of this source's traffic arrived on, e.g. `social` or `organic`. */
  medium?: string | null;
}

export interface GrowthCampaign {
  id: string | null;
  name: string | null;
  source: string | null;
  medium: string | null;
  visitors: number;
  registrationStarted: number;
  registrationCompleted: number;
  approved: number;
  conversionPct: number;
  createdAt: string | null;
}

export interface GrowthContent {
  contentKey: string | null;
  title: string | null;
  url: string | null;
  visitors: number;
  registrationStarted: number;
  registrationCompleted: number;
  approved: number;
  conversionPct: number;
  /** The source most of this content piece's traffic arrived on. */
  source?: string | null;
  /** The medium most of this content piece's traffic arrived on. */
  medium?: string | null;
}

export interface GrowthEvent {
  id: string;
  visitorId: string | null;
  eventName: string;
  pageUrl: string | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  referralCode: string | null;
  deviceCategory: string | null;
  country: string | null;
  userId: string | null;
  createdAt: string;
}

export interface Prospect {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  linkedInUrl: string | null;
  company: string | null;
  currentTitle: string | null;
  experienceYears: number | null;
  primaryTechnologyArea: string | null;
  message: string | null;
  source: string | null;
  sourceMedium: string | null;
  sourceCampaign: string | null;
  sourceContent: string | null;
  sourceReferralCode: string | null;
  capturedBy: string | null;
  capturedAt: string | null;
  consentStatus: string | null;
  consentToContact: boolean | null;
  communicationStatus: string | null;
  prospectStatus: string;
  invitationStatus: string | null;
  registrationStatus: string | null;
  notes: string | null;
  targetRole: TargetRole | null;
  userId: string | null;
  lastContactedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProspectRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  linkedInUrl?: string;
  company?: string;
  currentTitle?: string;
  experienceYears?: number | null;
  primaryTechnologyArea?: string;
  message?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  referralCode?: string;
  prospectStatus?: string;
  notes?: string;
  consentToContact?: boolean;
  targetRole?: TargetRole | '';
}

export interface InviteProspectRequest {
  createdBy?: string;
  ttlHours?: number;
  message?: string;
  targetRole?: TargetRole;
  accessTrack?: AccessTrack;
}

export interface GrowthInvitation {
  id: string;
  token: string;
  prospectId: string;
  prospectName: string | null;
  email: string | null;
  status: string;
  targetRole: TargetRole | null;
  accessTrack: AccessTrack | null;
  waitlistStatus: string | null;
  invitationUrl: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  acceptedAt: string | null;
  expiresAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

export type InviteStatus = 'ACTIVE' | 'USED' | 'EXPIRED' | 'REVOKED';

/** Result of converting an early-access (waitlist) signup into a Growth prospect. */
export interface EarlyAccessProspectConversion {
  prospect: Prospect;
  /** Null when the invitation could not be issued; see {@link warning}. */
  invitation: GrowthInvitation | null;
  warning: string | null;
}

export type RegistrationStatus = 'NOT_REGISTERED' | 'REGISTERED';

export type ApprovalStatus = 'NOT_REGISTERED' | 'PENDING' | 'APPROVED';

/** One invite link, as seen by the admin referrals screen. */
export interface PrivateInvitationAdminRow {
  invitationId: string;
  inviteCode: string;
  /** Raw column value; lags behind for an invite that aged out without being opened. */
  storedStatus: InviteStatus;
  /** What the link is worth right now, derived from its expiry. */
  invitationStatus: InviteStatus;
  used: boolean;
  revoked: boolean;
  revokedAt: string | null;
  inviteUrl: string | null;
  createdAt: string | null;
  expiryAt: string | null;

  invitedById: string;
  invitedByFirstName: string | null;
  invitedByLastName: string | null;
  invitedByEmail: string | null;
  invitedByRole: string | null;
  /** Credits the inviter has left to share. */
  invitedByInviteBalance: number | null;
  /**
   * Credits the inviter has ever been granted. Rises with admin top-ups, so it is the real
   * ceiling rather than the configured default.
   */
  invitedByInviteTotal: number | null;
  /** Every link the inviter has ever generated, ignoring the active filters. */
  invitedByTotalLinks: number | null;

  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  userCreatedAt: string | null;
  userStatus: string | null;

  registrationStatus: RegistrationStatus;
  approvalStatus: ApprovalStatus;
}

/**
 * Headline referral numbers for the filtered window. Carries no referrer list: Top Referrers is
 * paginated on its own so the two do not have to be refetched together.
 */
export interface PrivateReferralStats {
  totalCandidates: number;
  candidatesWithReferrals: number;
  totalInvitations: number;
  activeInvitations: number;
  usedInvitations: number;
  expiredInvitations: number;
  revokedInvitations: number;
  registeredInvitations: number;
  approvedInvitations: number;
  conversionPct: number;
  participationPct: number;
}

/** One candidate's referral performance, rolled up across every link they have shared. */
export interface TopReferrer {
  candidateId: string;
  candidateName: string;
  candidateEmail: string | null;
  role: string | null;
  referrerStatus: string | null;
  /** All-time invite count for this referrer, which equals their total links. */
  totalLinks: number;
  /** Credits the referrer has left to share. */
  inviteBalance: number | null;
  /**
   * Credits the referrer has ever been granted. Rises with admin top-ups, so it is the real
   * ceiling rather than the configured default.
   */
  inviteTotal: number | null;
  activeLinks: number;
  registered: number;
  approved: number;
  conversionPct: number;
  lastInviteAt: string | null;
  pendingInviteCodes: string[];
}

export interface ReferralAdminFilters {
  status?: InviteStatus;
  search?: string;
  isRegistered?: boolean;
  isApproved?: boolean;
  fromDate?: string;
  toDate?: string;
}

export interface RevokeInviteResponse {
  inviteCode: string;
  status: 'REVOKED';
  creditsRestored: number;
}

/* ==========================================================================
   Credit exhaustion
   --------------------------------------------------------------------------
   Candidates and recruiters both run out of credits, but for different reasons
   and with different consequences, so the types below model one row shape with
   nullable role-specific fields. The list hides a column per tab rather than
   printing "null", and the shared row shape is what lets one table renderer and
   one give-credits modal serve both tabs.
   ========================================================================== */

/** Which balance ran out. RECRUITER_JOB_CREDITS is the one that actually blocks. */
export type ExhaustionType = 'CANDIDATE_INVITE' | 'RECRUITER_INVITE' | 'RECRUITER_JOB_CREDITS';

/** Narrowing filter on the recruiter tab. BOTH means "out on invite and job credits". */
export type ExhaustionFilter = 'ALL' | 'INVITE' | 'JOB_CREDITS' | 'BOTH';

export interface LimitExhaustionRow {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  userStatus: string | null;
  createdAt: string | null;

  inviteBalance: number | null;
  inviteTotal: number | null;
  /** granted - remaining, i.e. credits genuinely spent. */
  creditsConsumed: number | null;

  /* candidate-only referral performance */
  accessTrack: string | null;
  referralCode: string | null;
  totalLinks: number | null;
  activeLinks: number | null;
  registeredCount: number | null;
  approvedCount: number | null;
  approvalPct: number | null;
  lastInviteAt: string | null;

  /* recruiter-only company and subscription context */
  companyId: string | null;
  companyName: string | null;
  companySizeRange: string | null;
  /** null when the recruiter has no company row; a 0 there would read as "out of credits". */
  jobCreditsBalance: number | null;
  trialUsed: boolean | null;
  subscriptionPlanName: string | null;
  subscriptionPlanStatus: string | null;
  subscriptionPlanExpireDate: string | null;
  isCompanyAdmin: boolean | null;

  exhaustionTypes: ExhaustionType[];
  inviteAlertCount: number | null;
  jobAlertCount: number | null;
  exhaustedAt: string | null;
  daysSinceExhausted: number | null;
  /** How many times admins were told. Guards against the scanner re-notifying. */
  notificationCount: number | null;
  refillCount: number | null;
  creditsGranted: number | null;

  /* recruiter-only outreach state */
  outreachCount: number | null;
  emailSentCount: number | null;
  notificationSentCount: number | null;
  lastOutreachAt: string | null;
}

export interface LimitExhaustionCandidateStats {
  totalCandidates: number;
  exhaustedCandidates: number;
  exhaustedPctOfBase: number;

  exhaustedLast7Days: number;
  exhaustedLast30Days: number;
  /** Exhaustion velocity: share of the whole base that ran dry in the last week. */
  exhaustedLast7DaysPctOfBase: number;

  referralLinks: number;
  referralRegistrations: number;
  referralApprovals: number;
  referralApprovalPct: number;
  /** Exhausted candidates who brought in at least one person - the ones worth keeping. */
  referrerExhaustedCount: number;
  referrerSharePct: number;

  avgDaysToExhaust: number;
  refills: number;
  creditsGranted: number;
}

export interface LimitExhaustionRecruiterStats {
  totalRecruiters: number;
  exhaustedRecruiters: number;
  exhaustedPctOfBase: number;

  inviteExhaustedCount: number;
  jobCreditsExhaustedCount: number;

  trialUsedCount: number;
  /** Had a real plan at some point, so out of credits means out of renewal. */
  paidSubscriberExhaustedCount: number;
  /** Only ever had the trial, so out of credits means out of first purchase. */
  neverPaidExhaustedCount: number;
  trialExpiredCount: number;
  /** Paid and still inside the plan window, yet at zero credits - most urgent. */
  activeSubscriberExhaustedCount: number;

  exhaustedLast7Days: number;
  exhaustedLast30Days: number;
  exhaustedLast30DaysPctOfExhausted: number;
  avgDaysToExhaust: number;

  contactedCount: number;
  contactedPct: number;
  sendsLast30Days: number;
  emailSentCount: number;
  notificationSentCount: number;
  emailFailedCount: number;
  /** Exhausted recruiters never contacted - the actual work queue. */
  notYetContactedCount: number;

  refills: number;
  creditsGranted: number;
}

export interface LimitAlertSummary {
  candidateExhausted: number;
  recruiterExhausted: number;
  recruiterNotContacted: number;
  totalOpen: number;
  unnotifiedCount: number;
}

/** Pre-filled copy an outreach modal opens with, ready to be edited before sending. */
export interface LimitOutreachDraft {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  exhaustionType: ExhaustionType | null;
  /** READABLE_INVITE | JOB_CREDITS | RECRUITER_INVITE */
  trigger: string | null;
  subject: string;
  body: string;
  inviteBalance: number | null;
  inviteTotal: number | null;
  jobCreditsBalance: number | null;
  subscriptionPlanName: string | null;
  companyName: string | null;
  /** Tokens the admin may keep in the body; substituted at send time. */
  availableTokens: string[];
}

export interface LimitOutreachRequest {
  userId: string;
  exhaustionType?: ExhaustionType;
  subject?: string;
  body?: string;
  /** Both default to true server-side; an admin only ever deliberately drops one. */
  sendEmail?: boolean;
  sendNotification?: boolean;
}

export interface LimitOutreachResult {
  outreachId: string | null;
  userId: string | null;
  email: string | null;
  emailSent: boolean;
  emailStatus: string | null;
  emailError: string | null;
  notificationSent: boolean;
  notificationError: string | null;
  subject: string | null;
  message: string | null;
}

export interface LimitOutreachHistoryRow {
  outreachId: string;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  exhaustionType: ExhaustionType | null;
  subject: string | null;
  /** Stored copy, not a regenerated template - this is what actually left the outbox. */
  body: string | null;
  emailSent: boolean;
  notificationSent: boolean;
  emailStatus: string | null;
  emailError: string | null;
  sentTo: string | null;
  sentBy: string | null;
  createdAt: string | null;
}

/** Query params for either listing. sortBy is whitelisted server-side. */
export interface LimitExhaustionFilters {
  search?: string;
  sortBy?: string;
  page?: number;
  size?: number;
}

export interface LimitRecruiterExhaustionFilters extends LimitExhaustionFilters {
  exhaustionType?: ExhaustionFilter;
}

export interface LimitCandidateExhaustionResponse
  extends PagedStatsResponse<LimitExhaustionRow, LimitExhaustionCandidateStats> {}

export interface LimitRecruiterExhaustionResponse
  extends PagedStatsResponse<LimitExhaustionRow, LimitExhaustionRecruiterStats> {}

export type LimitOutreachHistoryPage = PaginationResponse<LimitOutreachHistoryRow>;

export interface GrowthProspectsPage extends PaginationResponse<Prospect> {}
export interface GrowthInvitationsPage extends PaginationResponse<GrowthInvitation> {}
export interface GrowthEventsPage extends PaginationResponse<GrowthEvent> {}

export interface InvitationTrackResponse {
  id: string;
  token: string;
  status: string;
  firstName: string | null;
  currentTitle: string | null;
  company: string | null;
  registrationUrl: string | null;
  expiresAt: string | null;
  expired: boolean;
  accepted: boolean;
  /** null when the portal config does not lock the role to the invite. */
  targetRole: TargetRole | null;
  accessTrack: AccessTrack | null;
  waitlistStatus: string | null;
  accessLabel: string | null;
}

export interface TrackVisitorRequest {
  visitorId?: string;
  pageUrl?: string;
  landingPage?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  referralCode?: string;
  deviceCategory?: string;
  referrer?: string;
  country?: string;
  newSession?: boolean;
}

export interface TrackEventRequest {
  visitorId?: string;
  eventName?: string;
  pageUrl?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  referralCode?: string;
  deviceCategory?: string;
  userId?: string;
  metadata?: Record<string, string>;
}